import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertCircle, Download, Loader2, Users } from 'lucide-react';
import {
  Tabs,
  Tab,
  Box,
  CircularProgress,
  Menu,
  MenuItem,
  Button,
  Popover,
  Typography,
} from '@mui/material';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import { API_REQUEST } from '../../helpers/request';
import { API } from '../../constants';
import PropTypes from 'prop-types';
import { SECTION_CONFIG } from './sectionConfig';
import ScopeAnalysisSection from '../../components/scope/ScopeAnalysisSection';
import ScopeErrorHandler from '../../components/scope/ScopeErrorHandler';
import TenderLevellingResults from './TenderLevellingResults';
import TenderAnalysisResults from './TenderAnalysisResults';
import TenderErrorHandler from '../../components/tender/TenderErrorHandler';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DescriptionIcon from '@mui/icons-material/Description';
import { styled } from '@mui/material/styles';
import { parseError } from '../../helpers/errorHandler';
import colors from '../../theme/colors';
import { calculateProgress } from '../../helpers/progress';

// Status Display Component with Progress Bars
const StatusDisplay = ({
  status,
  message,
  userMessage,
  suggestions,
  tenderData,
  scopeData,
  levellingData,
  onViewDashboard,
}) => {
  const statusConfig = {
    IDLE: {
      icon: <Loader2 className="status-icon spinning" />,
      title: 'Loading Analysis Status',
      description: 'Checking analysis status...',
      variant: 'info',
      animationClass: 'pulse',
    },
    PENDING: {
      icon: <Loader2 className="status-icon spinning" />,
      title: 'Analysis Queued',
      description:
        'Your tender analysis is queued for processing. Please check back later.',
      variant: 'info',
      animationClass: 'pulse',
    },
    STARTED: {
      icon: <Loader2 className="status-icon spinning" />,
      title: 'Analysis in Progress',
      description:
        'Your tender analysis is being processed. Please check back later.',
      variant: 'info',
      animationClass: 'pulse',
    },
    FAILURE: {
      icon: <AlertCircle className="status-icon" />,
      title: 'Analysis Failed',
      description:
        userMessage ||
        message ||
        'There was an error processing your tender analysis. Please try again.',
      variant: 'danger',
      animationClass: 'shake',
    },
    UNPROCESSABLE: {
      icon: <AlertCircle className="status-icon" />,
      title: 'Analysis Incomplete',
      description:
        userMessage ||
        message ||
        'The analysis completed but some data could not be processed. Please review the partial results below.',
      variant: 'warning',
      animationClass: 'none',
    },
  };

  const config = statusConfig[status];
  if (!config) return null;

  // Show simple loading screen when IDLE
  if (status === 'IDLE') {
    return <SimpleLoadingScreen message="Loading analysis status..." />;
  }

  const tenderProgress = calculateProgress(
    tenderData?.status,
    tenderData?.current_step,
    tenderData?.total_steps
  );
  const scopeProgress = scopeData
    ? calculateProgress(
        scopeData.status,
        scopeData.current_step,
        scopeData.total_steps
      )
    : 0;
  const levellingProgress = levellingData
    ? calculateProgress(
        levellingData.status,
        levellingData.current_step,
        levellingData.total_steps
      )
    : 0;

  // Get progress bar color based on status
  const getProgressBarColor = (data) => {
    if (!data) return colors.blue.main; // Default blue for in-progress
    if (data.status === 'SUCCESS') return colors.green.main; // Green for success
    if (data.status === 'FAILURE') return colors.red.main; // Red for failure
    if (data.status === 'UNPROCESSABLE') return colors.orange.warning; // Orange for partial
    return colors.blue.main; // Blue for pending/started
  };

  // Get status text for each analysis
  const getStatusText = (data, analysisName) => {
    if (!data) return 'Not started';

    const status = data.status;
    const stepMap = {
      IDLE: 'Loading...',
      PENDING: 'Queued',
      STARTED: `Step ${data.current_step || 0} of ${data.total_steps || 1}`,
      SUCCESS: '✓ Completed',
      FAILURE: '✗ Failed',
      UNPROCESSABLE: '⚠ Partial',
      NOT_FOUND: 'Not available',
    };

    return stepMap[status] || analysisName;
  };

  // Get status icon for each analysis
  const getStatusIcon = (data) => {
    if (!data) return null;

    switch (data.status) {
      case 'IDLE':
      case 'PENDING':
      case 'STARTED':
        return (
          <Loader2
            className="w-3 h-3 spinning mr-1"
            style={{ color: colors.blue.main }}
          />
        );
      case 'SUCCESS':
        return (
          <CheckCircleOutlinedIcon
            sx={{
              fontSize: '0.75rem',
              color: colors.green.main,
              marginRight: '4px',
            }}
          />
        );
      case 'FAILURE':
        return (
          <AlertCircle
            className="w-3 h-3 mr-1"
            style={{ color: colors.red.main }}
          />
        );
      case 'UNPROCESSABLE':
        return (
          <AlertCircle
            className="w-3 h-3 mr-1"
            style={{ color: colors.orange.warning }}
          />
        );
      case 'NOT_FOUND':
        return (
          <AlertCircle
            className="w-3 h-3 mr-1"
            style={{ color: colors.grey[500] }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      className={`status-display status-${config.variant} ${config.animationClass}`}
    >
      <div className="status-container">
        <div className="status-content">
          <h2 className="status-title">{config.title}</h2>
          <p className="status-description">{config.description}</p>

          {/* Dashboard Information and Button */}
          {onViewDashboard && status !== 'IDLE' && (
            <div className="dashboard-action-section">
              <p className="dashboard-info-text">
                <strong>
                  The analysis might take a few minutes to complete
                </strong>
                <br />
                • It&apos;s safe to leave this page
                <br />
                • You can check the results on the dashboard where the status
                will be shown
                <br />• Once ready, the status will be indicated in green
              </p>
              <Button onClick={onViewDashboard} variant="contained">
                View Dashboard
              </Button>
            </div>
          )}

          {/* Progress Bars Section - Only show when not IDLE */}
          {status !== 'IDLE' && tenderData && tenderData.status !== 'IDLE' && (
            <div className="progress-bars-section">
              {/* Tender Analysis Progress */}
              {tenderData && tenderData.status !== 'IDLE' && (
                <div className="progress-item">
                  <div className="progress-header">
                    <span className="progress-label">
                      {getStatusIcon(tenderData)}
                      Tender Analysis
                    </span>
                    <span className="progress-percentage">
                      {Math.round(tenderProgress)}%
                    </span>
                  </div>
                  <div className="progress-bar-container">
                    <div
                      className="progress-bar-fill"
                      style={{
                        width: `${tenderProgress}%`,
                        backgroundColor: getProgressBarColor(tenderData),
                      }}
                    />
                  </div>
                  <div className="progress-step">
                    {getStatusText(tenderData, 'Tender Analysis')}
                  </div>
                </div>
              )}

              {/* Scope Analysis Progress */}
              {scopeData &&
                scopeData.status !== 'NOT_FOUND' &&
                scopeData.status !== 'IDLE' && (
                  <div className="progress-item">
                    <div className="progress-header">
                      <span className="progress-label">
                        {getStatusIcon(scopeData)}
                        Scope Analysis
                      </span>
                      <span className="progress-percentage">
                        {Math.round(scopeProgress)}%
                      </span>
                    </div>
                    <div className="progress-bar-container">
                      <div
                        className="progress-bar-fill"
                        style={{
                          width: `${scopeProgress}%`,
                          backgroundColor: getProgressBarColor(scopeData),
                        }}
                      />
                    </div>
                    <div className="progress-step">
                      {getStatusText(scopeData, 'Scope Analysis')}
                    </div>
                  </div>
                )}

              {/* Tender Levelling Progress */}
              {levellingData &&
                levellingData.status !== 'NOT_FOUND' &&
                levellingData.status !== 'IDLE' && (
                  <div className="progress-item">
                    <div className="progress-header">
                      <span className="progress-label">
                        {getStatusIcon(levellingData)}
                        Levelling Analysis
                      </span>
                      <span className="progress-percentage">
                        {Math.round(levellingProgress)}%
                      </span>
                    </div>
                    <div className="progress-bar-container">
                      <div
                        className="progress-bar-fill"
                        style={{
                          width: `${levellingProgress}%`,
                          backgroundColor: getProgressBarColor(levellingData),
                        }}
                      />
                    </div>
                    <div className="progress-step">
                      {getStatusText(levellingData, 'Levelling Analysis')}
                    </div>
                  </div>
                )}
            </div>
          )}
          {suggestions && suggestions.length > 0 && (
            <div className="suggestions-section">
              <h3 className="suggestions-title">What can you do?</h3>
              <div className="suggestions-list">
                {suggestions.map((suggestion, index) => (
                  <div key={index} className="suggestion-item">
                    <span className="suggestion-number">{index + 1}</span>
                    <span className="suggestion-text">{suggestion}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {status === 'FAILURE' && (
        <div className="error-details-footer">
          <p className="error-code">
            Error Reference: {Date.now().toString(36).toUpperCase()}
          </p>
          <p className="support-text">
            Need help?{' '}
            <a href="/help-support" className="support-link">
              Contact Support
            </a>
          </p>
        </div>
      )}
    </div>
  );
};

StatusDisplay.propTypes = {
  status: PropTypes.string.isRequired,
  message: PropTypes.string,
  userMessage: PropTypes.string,
  suggestions: PropTypes.arrayOf(PropTypes.string),
  tenderData: PropTypes.object,
  scopeData: PropTypes.object,
  levellingData: PropTypes.object,
  onViewDashboard: PropTypes.func,
};

const BootstrapButton = styled(Button)({
  boxShadow: 'none',
  textTransform: 'none',
  fontSize: 14,
  padding: '5px 12px',
  border: '1px solid',
  lineHeight: 1.5,
  backgroundColor: colors.background.paper,
  borderColor: colors.grey.t600,
  color: colors.secondary.contrast,
  fontFamily: [
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
  ].join(','),
  '&:hover': {
    backgroundColor: colors.grey[100],
    borderColor: colors.grey[600],
    boxShadow: 'none',
  },
  '&:active': {
    boxShadow: 'none',
    backgroundColor: colors.background.paper,
    borderColor: colors.grey[500],
  },
});

// Error Display Component
const SimpleErrorDisplay = ({ message }) => (
  <div className="analysis-container">
    <div className="error">
      <AlertCircle className="w-5 h-5" />
      {message}
    </div>
  </div>
);

SimpleErrorDisplay.propTypes = {
  message: PropTypes.string.isRequired,
};

// ==================== SIMPLE LOADING SCREEN ====================
const SimpleLoadingScreen = ({ message = 'Loading projects...' }) => (
  <Box
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: colors.grey[100],
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 'calc(100vh - 80px)', // Account for header height
      zIndex: 1,
    }}
  >
    <CircularProgress
      sx={{
        color: colors.primary.main, // Light blue/teal color
        mb: 2,
      }}
      size={48}
      thickness={4}
    />
    <Typography
      variant="body1"
      sx={{
        color: colors.text.secondary,
        fontSize: '1rem',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        fontWeight: 400,
      }}
    >
      {message}
    </Typography>
  </Box>
);

SimpleLoadingScreen.propTypes = {
  message: PropTypes.string,
};

// Custom hook for polling tender analysis status with progress tracking
const useTenderAnalysisStatus = (projectId, packageId) => {
  const [status, setStatus] = useState('IDLE');
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [totalSteps, setTotalSteps] = useState(0);

  useEffect(() => {
    if (!projectId || !packageId) return;

    const checkStatus = async () => {
      try {
        const response = await API_REQUEST.get(
          `/projects/${projectId}/packages/${packageId}/tender_analysis`
        );
        const newData = response.data;
        const newStatus = newData.status;
        const newCurrentStep = newData.current_step || 0;
        const newTotalSteps = newData.total_steps || 0;

        // Update progress
        const newProgress = calculateProgress(
          newStatus,
          newCurrentStep,
          newTotalSteps
        );
        setProgress(newProgress);
        setCurrentStep(newCurrentStep);
        setTotalSteps(newTotalSteps);

        setStatus(newStatus);
        setData(newData);

        if (newStatus === 'FAILURE') {
          const errorMessage =
            newData?.user_message ||
            'The analysis process encountered an error.';
          setError(errorMessage);
        }
      } catch (err) {
        setStatus('FAILURE');
        setError(err.message || 'Status check failed');
      }
    };

    // Poll every 5 seconds if not in final state (including IDLE)
    if (!['SUCCESS', 'FAILURE', 'UNPROCESSABLE'].includes(status)) {
      checkStatus();
      const statusInterval = setInterval(checkStatus, 5000);
      return () => clearInterval(statusInterval);
    } else {
      // Still check once even if in final state to get latest data
      checkStatus();
    }
  }, [projectId, packageId, status]);

  return { status, error, data, progress, currentStep, totalSteps };
};

// Custom hook for scope analysis status with progress tracking - FIXED 404 HANDLING
const useScopeAnalysisStatus = (projectId, packageId) => {
  const [status, setStatus] = useState('IDLE');
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [exists, setExists] = useState(true); // Assume it exists initially for polling
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [totalSteps, setTotalSteps] = useState(0);

  useEffect(() => {
    if (!projectId || !packageId) return;

    let isMounted = true;

    const checkStatus = async () => {
      try {
        const response = await API_REQUEST.get(
          `/projects/${projectId}/packages/${packageId}/scope_analysis`
        );

        if (!isMounted) return;

        const newData = response.data;
        const newStatus = newData.status;
        const newCurrentStep = newData.current_step || 0;
        const newTotalSteps = newData.total_steps || 0;

        // Calculate progress
        const newProgress = calculateProgress(
          newStatus,
          newCurrentStep,
          newTotalSteps
        );
        setProgress(newProgress);
        setCurrentStep(newCurrentStep);
        setTotalSteps(newTotalSteps);

        setExists(true);
        setStatus(newStatus);
        setData(newData);

        if (newStatus === 'FAILURE') {
          const errorMessage =
            newData?.user_message ||
            'The scope analysis process encountered an error.';
          setError(errorMessage);
        }
      } catch (err) {
        if (!isMounted) return;
        if (err.response?.status === 404) {
          // 404 means the analysis doesn't exist - stop polling and hide the tab
          setExists(false);
          setStatus('NOT_FOUND');
          setData(null);
          setError(parseError(err)); // Clear any previous errors
        } else {
          // Other errors - show error but keep the tab visible
          setStatus('FAILURE');
          setError(err.message || 'Status check failed');
        }
      }
    };

    // Only poll if we think it exists and is not in final state
    if (exists && !['SUCCESS', 'FAILURE', 'NOT_FOUND'].includes(status)) {
      checkStatus();
      const statusInterval = setInterval(checkStatus, 5000);
      return () => {
        isMounted = false;
        clearInterval(statusInterval);
      };
    } else if (exists) {
      // Check once even if in final state
      checkStatus();
    }

    return () => {
      isMounted = false;
    };
  }, [projectId, packageId, status, exists]);

  return { status, error, data, exists, progress, currentStep, totalSteps };
};

// Custom hook for tender levelling analysis status.
// hook: polls the results endpoint every 5s, treats a 404 as "analysis does not
// exist" (hides the tab — this is how the env-var gating surfaces in the UI),
// and stops polling once in a final state.
const useTenderLevellingAnalysisStatus = (projectId, packageId) => {
  const [status, setStatus] = useState('IDLE');
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [exists, setExists] = useState(true);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [totalSteps, setTotalSteps] = useState(0);

  useEffect(() => {
    if (!projectId || !packageId) return;

    let isMounted = true;

    const checkStatus = async () => {
      try {
        const response = await API_REQUEST.get(
          `/projects/${projectId}/packages/${packageId}/tender_levelling_analysis`
        );
        if (!isMounted) return;

        const newData = response.data;
        const newStatus = newData.status;
        const newCurrentStep = newData.current_step || 0;
        const newTotalSteps = newData.total_steps || 0;

        const newProgress = calculateProgress(
          newStatus,
          newCurrentStep,
          newTotalSteps
        );
        setProgress(newProgress);
        setCurrentStep(newCurrentStep);
        setTotalSteps(newTotalSteps);

        setExists(true);
        setStatus(newStatus);
        setData(newData);

        if (newStatus === 'FAILURE') {
          const errorMessage =
            newData?.user_message ||
            'The tender levelling analysis process encountered an error.';
          setError(errorMessage);
        }
      } catch (err) {
        if (!isMounted) return;

        if (err.response?.status === 404) {
          // 404 means the analysis doesn't exist - stop polling and hide the tab
          setExists(false);
          setStatus('NOT_FOUND');
          setData(null);
          setError(parseError(err));
        } else {
          setStatus('FAILURE');
          setError(err.message || 'Status check failed');
        }
      }
    };

    if (exists && !['SUCCESS', 'FAILURE', 'NOT_FOUND'].includes(status)) {
      checkStatus();
      const statusInterval = setInterval(checkStatus, 5000);
      return () => {
        isMounted = false;
        clearInterval(statusInterval);
      };
    } else if (exists) {
      checkStatus();
    }

    return () => {
      isMounted = false;
    };
  }, [projectId, packageId, status, exists]);

  return { status, error, data, exists, progress, currentStep, totalSteps };
};

// Progress bar component for scope analysis
const ScopeAnalysisProgress = ({ scopeData, isPolling }) => {
  if (!scopeData || !['PENDING', 'STARTED'].includes(scopeData.status)) {
    return null;
  }

  const getProgress = () => {
    const { status, current_step, total_steps } = scopeData;
    return calculateProgress(status, current_step, total_steps);
  };

  const getStatusText = () => {
    if (scopeData.status === 'PENDING') return 'Analysis queued...';
    if (scopeData.status === 'STARTED') {
      const { current_step = 0, total_steps = 1 } = scopeData;
      return `Analyzing documents... (Step ${current_step} of ${total_steps})`;
    }
    return 'Processing...';
  };

  return (
    <div className="scope-analysis-progress">
      <div className="progress-header">
        <div className="progress-text">
          <Loader2
            className={`progress-icon ${isPolling ? 'spinning' : ''}`}
            style={{ color: colors.blue.main }}
          />
          <span>{getStatusText()}</span>
        </div>
        <div className="progress-percentage">{Math.round(getProgress())}%</div>
      </div>
      <div className="progress-bar-container">
        <div
          className="progress-bar-fill"
          style={{
            width: `${getProgress()}%`,
            backgroundColor: colors.blue.main,
          }}
        />
      </div>
    </div>
  );
};

ScopeAnalysisProgress.propTypes = {
  scopeData: PropTypes.shape({
    status: PropTypes.string.isRequired,
    current_step: PropTypes.number,
    total_steps: PropTypes.number,
  }),
  isPolling: PropTypes.bool.isRequired,
};

// Export Button with Overview Component
const ExportButtonWithOverview = ({
  projectId,
  packageId,
  tenderStatus,
  scopeExists,
  scopeStatus,
  levellingExists,
  levellingStatus,
  subcontractors,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverEl, setPopoverEl] = useState(null);
  const open = Boolean(anchorEl);
  const popoverOpen = Boolean(popoverEl);

  const handleExportClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleOverviewClick = (event) => {
    setPopoverEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePopoverClose = () => {
    setPopoverEl(null);
  };

  const handleExport = (type) => {
    let url = '';
    switch (type) {
      case 'tender':
        url = API.url(
          `/projects/${projectId}/packages/${packageId}/tender_analysis/export/docx`
        );
        break;
      case 'scope':
        url = API.url(
          `/projects/${projectId}/packages/${packageId}/scope_analysis/export/docx`
        );
        break;
      case 'levelling':
        url = API.url(
          `/projects/${projectId}/packages/${packageId}/tender_levelling_analysis/export/xlsx`
        );
        break;
      default:
        return;
    }
    window.location.href = url;
    handleClose();
  };

  const exportOptions = [
    {
      key: 'tender',
      label: 'Export Tender Analysis',
      exists: true,
      isReady: ['SUCCESS'].includes(tenderStatus),
      disabled: [
        'IDLE',
        'PENDING',
        'STARTED',
        'FAILURE',
        'UNPROCESSABLE',
      ].includes(tenderStatus),
    },
    {
      key: 'scope',
      label: 'Export Scope of Works',
      exists: scopeExists,
      isReady: ['SUCCESS'].includes(scopeStatus),
      disabled:
        !scopeExists ||
        ['IDLE', 'PENDING', 'STARTED', 'FAILURE', 'UNPROCESSABLE'].includes(
          scopeStatus
        ),
    },
    {
      key: 'levelling',
      label: 'Export Levelling',
      exists: levellingExists,
      isReady: ['SUCCESS'].includes(levellingStatus),
      disabled:
        !levellingExists ||
        ['IDLE', 'PENDING', 'STARTED', 'FAILURE', 'UNPROCESSABLE'].includes(
          levellingStatus
        ),
    },
  ];

  const availableExports = exportOptions.filter((option) => option.exists);

  if (availableExports.length === 0) return null;

  // Compute totals for the overview header
  const totalFiles = subcontractors
    ? subcontractors.reduce((sum, s) => sum + (s.files ? s.files.length : 0), 0)
    : 0;
  const totalSubs = subcontractors ? subcontractors.length : 0;

  // Show overview popover content
  const renderOverviewContent = () => (
    <Box
      sx={{
        width: 380,
        maxHeight: 480,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: '1px solid',
          borderColor: colors.grey.t200,
          backgroundColor: colors.background.secondary,
        }}
      >
        <Typography
          sx={{
            fontSize: '0.9375rem',
            fontWeight: 600,
            color: colors.text.primary,
            mb: 0.5,
          }}
        >
          Package Overview
        </Typography>
        <Typography
          sx={{
            fontSize: '0.75rem',
            color: colors.grey.t500,
          }}
        >
          {totalSubs} subcontractor{totalSubs !== 1 ? 's' : ''} &middot;{' '}
          {totalFiles} file{totalFiles !== 1 ? 's' : ''}
        </Typography>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 2,
          py: 1.5,
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: colors.grey.t300,
            borderRadius: 2,
          },
        }}
      >
        {subcontractors && subcontractors.length > 0 ? (
          subcontractors.map((subcontractor, index) => (
            <Box
              key={index}
              sx={{
                mb: index < subcontractors.length - 1 ? 1.5 : 0,
                p: 1.5,
                borderRadius: '8px',
                border: '1px solid',
                borderColor: colors.grey.t200,
                backgroundColor: colors.background.paper,
                transition: 'border-color 0.15s ease',
                '&:hover': {
                  borderColor: colors.grey.t300,
                },
              }}
            >
              {/* Subcontractor header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb:
                    subcontractor.files && subcontractor.files.length > 0
                      ? 1
                      : 0,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '6px',
                      backgroundColor: colors.blue.lighter,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Users size={14} style={{ color: colors.blue.main }} />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      color: colors.text.primary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {subcontractor.name}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: '0.6875rem',
                    color: colors.grey.t500,
                    fontWeight: 500,
                    flexShrink: 0,
                    ml: 1,
                  }}
                >
                  {subcontractor.files ? subcontractor.files.length : 0} file
                  {subcontractor.files && subcontractor.files.length !== 1
                    ? 's'
                    : ''}
                </Typography>
              </Box>

              {/* Files list */}
              {subcontractor.files && subcontractor.files.length > 0 && (
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
                >
                  {subcontractor.files.map((file, fileIndex) => (
                    <Box
                      key={fileIndex}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        py: 0.5,
                        px: 1,
                        borderRadius: '5px',
                        backgroundColor: colors.background.default,
                      }}
                    >
                      <DescriptionIcon
                        sx={{
                          fontSize: 14,
                          color: colors.grey.t400,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: colors.grey.t700,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {file}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          ))
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 5,
            }}
          >
            <Users
              size={32}
              style={{ color: colors.grey.t300, marginBottom: 8 }}
            />
            <Typography sx={{ fontSize: '0.8125rem', color: colors.grey.t500 }}>
              No subcontractors found
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <div className="export-container-with-overview">
      <div className="export-buttons-group">
        <BootstrapButton
          style={{ marginRight: '15px' }}
          onClick={handleOverviewClick}
          startIcon={<Users size={15} />}
        >
          <span>Package Overview</span>
        </BootstrapButton>

        <BootstrapButton
          className="export-button"
          onClick={handleExportClick}
          endIcon={<KeyboardArrowDownIcon />}
          startIcon={<Download size={15} />}
        >
          <span>Exports</span>
        </BootstrapButton>
      </div>

      {/* Overview Popover */}
      <Popover
        open={popoverOpen}
        anchorEl={popoverEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '10px',
              boxShadow:
                '0 4px 24px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04)',
              overflow: 'hidden',
              mt: 0.5,
            },
          },
        }}
      >
        {renderOverviewContent()}
      </Popover>

      {/* Exports Menu */}
      <Menu
        id="export-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {availableExports.map((option) => (
          <MenuItem
            key={option.key}
            onClick={() => handleExport(option.key)}
            disabled={option.disabled}
            style={{
              fontSize: '14px',
              padding: '8px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              opacity: option.disabled ? 0.6 : 1,
            }}
          >
            {option.disabled &&
              ['IDLE', 'PENDING', 'STARTED'].includes(
                option.key === 'tender'
                  ? tenderStatus
                  : option.key === 'scope'
                    ? scopeStatus
                    : levellingStatus
              ) && <CircularProgress size={16} thickness={3} />}
            {option.isReady && !option.disabled && (
              <Download size={14} sx={{ color: 'success.main' }} />
            )}
            <span>{option.label}</span>
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

ExportButtonWithOverview.propTypes = {
  projectId: PropTypes.string.isRequired,
  packageId: PropTypes.string.isRequired,
  tenderStatus: PropTypes.string.isRequired,
  scopeExists: PropTypes.bool.isRequired,
  scopeStatus: PropTypes.string.isRequired,
  levellingExists: PropTypes.bool.isRequired,
  levellingStatus: PropTypes.string.isRequired,
  subcontractors: PropTypes.array.isRequired,
};

// Main Analysis Results Component
const AnalysisResults = () => {
  const { id: projectId, packageId } = useParams();
  const navigate = useNavigate();

  const [state, setState] = useState({
    projectData: null,
    packageData: null,
    subcontractors: [],
    marketConfigs: [],
    loading: true,
    error: null,
  });

  // Use polling hooks for status monitoring with progress tracking
  const {
    status: tenderStatus,
    error: tenderError,
    data: tenderData,
  } = useTenderAnalysisStatus(projectId, packageId);

  const {
    status: scopeStatus,
    error: scopeError,
    data: scopeData,
    exists: scopeExists,
  } = useScopeAnalysisStatus(projectId, packageId);
  const {
    status: levellingStatus,
    error: levellingError,
    data: levellingData,
    exists: levellingExists,
  } = useTenderLevellingAnalysisStatus(projectId, packageId);

  const [activeTab, setActiveTab] = useState(0);

  // Fetch initial project and package data only
  useEffect(() => {
    const fetchData = async () => {
      if (
        !projectId ||
        projectId === 'undefined' ||
        !packageId ||
        packageId === 'undefined'
      ) {
        setState((prev) => ({
          ...prev,
          error: 'Failed to load data. Please try again.',
          loading: false,
        }));
        return;
      }

      try {
        setState((prev) => ({ ...prev, loading: true, error: null }));

        const [projectRes, packageRes, quotesRes, marketConfigsRes] =
          await Promise.all([
            API_REQUEST.get(`projects/${projectId}`),
            API_REQUEST.get(`projects/${projectId}/packages/${packageId}`),
            API_REQUEST.get(
              `projects/${projectId}/packages/${packageId}/quotes`
            ),
            API_REQUEST.get('config/markets'),
          ]);

        const subcontractors = API_REQUEST.isSuccess(quotesRes.status)
          ? quotesRes.data.reduce((acc, quote) => {
              const existingIndex = acc.findIndex(
                (item) => item.name === quote.subcontractor_name
              );
              if (existingIndex >= 0) {
                acc[existingIndex].files.push(quote.file_name);
              } else {
                acc.push({
                  name: quote.subcontractor_name,
                  files: [quote.file_name],
                });
              }
              return acc;
            }, [])
          : [];

        setState({
          projectData: projectRes.data,
          packageData: packageRes.data,
          subcontractors,
          marketConfigs: API_REQUEST.isSuccess(marketConfigsRes.status)
            ? marketConfigsRes.data.markets
            : [],
          loading: false,
          error: null,
        });
      } catch (_err) {
        setState((prev) => ({
          ...prev,
          error: 'Failed to load data. Please try again.',
          loading: false,
        }));
      }
    };

    fetchData();
  }, [projectId, packageId]);

  const { projectData, packageData, subcontractors, marketConfigs } = state;

  // Get tax term based on market config
  const taxTerm = useMemo(() => {
    if (projectData?.market_code && marketConfigs?.length > 0) {
      const currentMarketConfig = marketConfigs.find(
        (mc) => mc.value === projectData.market_code
      );
      return currentMarketConfig?.tax_term || 'Tax';
    }
    return 'Tax';
  }, [projectData, marketConfigs]);

  // Dashboard navigation function
  const handleViewDashboard = () => {
    navigate('/dashboard');
  };

  const hasTenderPreview = useMemo(() => {
    if (!tenderData) return false;

    return SECTION_CONFIG.sectionOrder
      .filter((section) => section !== 'scope_analysis')
      .some((section) => Boolean(tenderData[section]));
  }, [tenderData]);

  // Determine if we should show StatusDisplay
  const showStatusDisplay = useMemo(() => {
    const tenderInProgress = !['SUCCESS', 'FAILURE', 'UNPROCESSABLE'].includes(
      tenderStatus
    );
    return tenderInProgress && !hasTenderPreview;
  }, [tenderStatus, hasTenderPreview]);

  // Determine if we should show Tabs
  const showTabs = useMemo(() => {
    return (
      projectData &&
      (['SUCCESS', 'FAILURE', 'UNPROCESSABLE'].includes(tenderStatus) ||
        hasTenderPreview)
    );
  }, [projectData, tenderStatus, hasTenderPreview]);

  const tenderSectionKeys = useMemo(
    () =>
      SECTION_CONFIG.sectionOrder.filter(
        (section) => section !== 'scope_analysis'
      ),
    []
  );

  const completedTenderSections = useMemo(() => {
    if (!tenderData) return 0;

    return tenderSectionKeys.filter((section) => Boolean(tenderData[section]))
      .length;
  }, [tenderData, tenderSectionKeys]);

  const showInlineTenderPreview = useMemo(() => {
    return (
      hasTenderPreview &&
      !['SUCCESS', 'FAILURE', 'UNPROCESSABLE'].includes(tenderStatus)
    );
  }, [hasTenderPreview, tenderStatus]);

  const tenderPreviewProgress = useMemo(() => {
    if (tenderData?.total_steps > 0) {
      return calculateProgress(
        tenderStatus,
        tenderData?.current_step,
        tenderData?.total_steps
      );
    }

    if (tenderSectionKeys.length === 0) return 0;
    return Math.round(
      (completedTenderSections / tenderSectionKeys.length) * 100
    );
  }, [
    tenderData?.total_steps,
    tenderData?.current_step,
    tenderStatus,
    completedTenderSections,
    tenderSectionKeys,
  ]);

  // Dynamic tab configuration
  const tabs = useMemo(() => {
    const getTabIcon = (status) => {
      if (status === 'IDLE' || status === 'PENDING' || status === 'STARTED') {
        return (
          <CircularProgress
            size={15}
            thickness={3}
            sx={{ color: colors.blue.main }}
          />
        );
      } else if (status === 'SUCCESS') {
        return (
          <CheckCircleOutlinedIcon
            fontSize="small"
            sx={{ color: colors.green.main }}
          />
        );
      } else if (status === 'FAILURE') {
        return <AlertCircle size={15} style={{ color: colors.red.main }} />;
      } else {
        return (
          <CheckCircleOutlinedIcon
            fontSize="small"
            sx={{ color: colors.green.main }}
          />
        );
      }
    };

    const tabList = [
      {
        key: 'tender',
        label: 'Tender Analysis',
        icon: getTabIcon(tenderStatus),
      },
    ];

    // Show scope tab only if scope analysis exists (not 404)
    if (scopeExists) {
      tabList.push({
        key: 'scope',
        label: 'Scope of Works Analysis',
        icon: getTabIcon(scopeStatus),
      });
    }

    // Show levelling tab only if tender levelling analysis exists (not 404)
    if (levellingExists) {
      tabList.push({
        key: 'levelling',
        label: 'Levelling',
        icon: getTabIcon(levellingStatus),
      });
    }

    return tabList;
  }, [
    scopeExists,
    levellingExists,
    tenderStatus,
    scopeStatus,
    levellingStatus,
  ]);

  // Get current active tab key
  const activeTabKey = useMemo(() => {
    return tabs[activeTab]?.key || 'tender';
  }, [tabs, activeTab]);

  // Show the export button when ANY analysis has a successful, exportable
  // result — not only tender. (Otherwise a levelling-only package would hide the
  // export menu even though there's something to download.)
  const showResults =
    tenderStatus === 'SUCCESS' ||
    (levellingExists && levellingStatus === 'SUCCESS');

  return (
    <div className="analysis-wrapper" style={{ position: 'relative' }}>
      <div className="analysis-container">
        {/* Header Section */}
        <div className="header">
          <h1 className="title">{projectData?.name || 'Project Details'}</h1>
          <div className="project-info">
            <div className="metadata">
              <div className="info-item">
                <strong>Package name:</strong>{' '}
                {packageData?.name || 'Loading...'}
              </div>
              <div className="info-item">
                <strong>Created:</strong>{' '}
                {packageData?.created
                  ? new Date(packageData.created).toLocaleDateString('en-GB')
                  : 'Loading...'}
              </div>
              <div className="export-wrapper">
                {showResults && (
                  <ExportButtonWithOverview
                    projectId={projectId}
                    packageId={packageId}
                    tenderStatus={tenderStatus}
                    scopeExists={scopeExists}
                    scopeStatus={scopeStatus}
                    levellingExists={levellingExists}
                    levellingStatus={levellingStatus}
                    subcontractors={subcontractors}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Status Display for In-Progress Analysis */}
        {showStatusDisplay && (
          <StatusDisplay
            status={tenderStatus}
            message={tenderError}
            userMessage={tenderData?.user_message}
            suggestions={tenderData?.suggestions}
            tenderData={tenderData}
            scopeData={scopeData}
            levellingData={levellingData}
            onViewDashboard={handleViewDashboard}
          />
        )}

        {/* Analysis Tabs */}
        {showTabs && (
          <>
            {/* Show tabs only if we have multiple tabs */}
            {tabs.length > 1 && (
              <Box sx={{ mb: 2 }}>
                <Tabs
                  value={activeTab}
                  onChange={(e, newVal) => setActiveTab(newVal)}
                  aria-label="Analysis tabs"
                >
                  {tabs.map((tab) => (
                    <Tab
                      key={tab.key}
                      label={tab.label}
                      icon={tab.icon}
                      iconPosition="end"
                    />
                  ))}
                </Tabs>
              </Box>
            )}

            {/* Tender Analysis Tab */}
            {(activeTabKey === 'tender' || tabs.length === 1) && (
              <>
                {tabs.length === 1 && (
                  <h2 className="project-analysis-title">Tender Analysis</h2>
                )}

                {tenderStatus === 'FAILURE' ||
                tenderStatus === 'UNPROCESSABLE' ? (
                  <TenderErrorHandler
                    status={tenderStatus}
                    error={tenderError}
                    userMessage={tenderData?.user_message}
                    suggestions={tenderData?.suggestions}
                    canRetry={tenderData?.can_retry}
                    errorType={tenderData?.error_type}
                  />
                ) : (
                  <>
                    {showInlineTenderPreview && (
                      <div
                        style={{
                          padding: '12px 16px',
                          marginBottom: '14px',
                          borderRadius: '8px',
                          border: `1px solid ${colors.grey.t200}`,
                          backgroundColor: colors.background.paper,
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '8px',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              color: colors.text.secondary,
                              fontSize: '0.9rem',
                              fontWeight: 500,
                            }}
                          >
                            <Loader2 className="status-icon spinning" />
                            <span>
                              Analysis in progress - sections appear as they
                              complete
                            </span>
                          </div>
                          <span
                            style={{
                              color: colors.text.secondary,
                              fontSize: '0.85rem',
                              fontWeight: 600,
                            }}
                          >
                            {completedTenderSections}/{tenderSectionKeys.length}
                          </span>
                        </div>

                        <div
                          style={{
                            width: '100%',
                            height: '6px',
                            borderRadius: '999px',
                            backgroundColor: colors.grey.t100,
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              width: `${tenderPreviewProgress}%`,
                              height: '100%',
                              backgroundColor: colors.blue.main,
                              transition: 'width 240ms ease',
                            }}
                          />
                        </div>
                      </div>
                    )}

                    <TenderAnalysisResults
                      tenderData={tenderData}
                      status={tenderStatus}
                      taxTerm={taxTerm}
                      subcontractors={subcontractors}
                      boqFileName={tenderData?.boq_file_name}
                    />
                  </>
                )}
              </>
            )}

            {/* Scope Analysis Tab */}
            {activeTabKey === 'scope' && (
              <div className="scope-tab-container">
                <ScopeAnalysisProgress
                  scopeData={scopeData}
                  isPolling={
                    !['SUCCESS', 'FAILURE', 'NOT_FOUND'].includes(scopeStatus)
                  }
                />

                {scopeError ? (
                  <ScopeErrorHandler
                    status={scopeStatus}
                    error={scopeError}
                    hasQuotes={subcontractors.length > 0}
                    hasScopeFile={packageData?.scope_of_works}
                    packageData={packageData}
                    userMessage={scopeData?.user_message}
                    suggestions={scopeData?.suggestions}
                    canRetry={scopeData?.can_retry}
                  />
                ) : (
                  <ScopeAnalysisSection
                    scopeAnalysisData={scopeData}
                    isLoading={
                      !['SUCCESS', 'FAILURE', 'NOT_FOUND'].includes(scopeStatus)
                    }
                  />
                )}
              </div>
            )}

            {/* Tender Levelling Tab */}
            {activeTabKey === 'levelling' && (
              <div className="levelling-tab-container">
                {levellingError && levellingStatus === 'FAILURE' ? (
                  <Box sx={{ p: 3 }}>
                    <Typography color="error" sx={{ fontWeight: 600 }}>
                      Tender levelling analysis failed
                    </Typography>
                    <Typography color="text.secondary" sx={{ mt: 1 }}>
                      {levellingError}
                    </Typography>
                  </Box>
                ) : (
                  <TenderLevellingResults
                    levellingData={levellingData?.analysis_results || null}
                    status={levellingStatus}
                    progressData={levellingData}
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AnalysisResults;
